import TableRow from '@material-ui/core/TableRow';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { ILPULine } from '../../../../@types/model/transformer/tariffs/tariffs';
import { ITransformerInvoice, TransformerInvoiceHelper } from '../../../../@types/model/transformer/invoice/transformerInvoice';
import { DATE_INPUT_FORMAT_DEFAULT, DATE_REGEX } from '../../../../appConstants';
import { TableCell } from '@material-ui/core';
import * as lodash from 'lodash';
import { usePrevious } from '../../../../hooks/query/usePrevious';
import InputMask from '../../../customComponents/InputMask';
import DateTextInput from '../../../customComponents/input/DateTextInput';

interface ILPUCapturingFormTableRowProps {
    invoice : ITransformerInvoice<ILPULine>;
    index : number;
    lineIndex : number;
    line : ILPULine;

    isSubmitting ?: boolean;
    handleChange(e : React.ChangeEvent<unknown>) : void;
    handleBlur(e : React.FocusEvent<unknown>) : void;
    setFieldValue : (field : string, value : unknown, shouldValidate ?: boolean) => void;

    multi ?: boolean;
    selected ?: boolean;

    serviceCharge : number;
    standardConnectionCharge : number;

    disabled : boolean;
}

const LPUCapturingFormTableRow = (props : ILPUCapturingFormTableRowProps) : React.ReactElement => {

    useEffect(() => {
        if (!props.line.isTotalsManuallyOverridden && lineIndex == 0) {
            calculateTotal();
        }
    }, [lodash.isEqual(props.invoice, usePrevious(props.invoice)),]);

    const calculateTotal = () : void  => {
        const value = TransformerInvoiceHelper.calculateLPUTotal(props.invoice);
        props.setFieldValue(`${props.index}`, value, true);
    };

    const onPreventDecimal = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === '.') {
            event.preventDefault();
            return;
        }

        onPreventEnter(event);
    };

    const onPreventEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const onFieldChange = (event : React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.currentTarget;
        if (!value) return;

        props.setFieldValue(`${index}.lines[${lineIndex}].isTotalsManuallyOverridden`, false);
        props.setFieldValue(name, Number(value));
    };

    const handleManualChange = (event : React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.currentTarget;
        if (!value) return;
        
        props.setFieldValue(`${index}.lines[${lineIndex}].isTotalsManuallyOverridden`, true);
        props.setFieldValue(name, Number(value));
    };

    const isDueDateValid = useMemo<boolean>(() => {
        return DATE_REGEX.test(props.invoice.paymentDueDate ?? '') && moment.utc(props.invoice.paymentDueDate ?? '', DATE_INPUT_FORMAT_DEFAULT, true).isValid();
    }, [props.invoice]);

    const {
        line: value,
        index,
        isSubmitting,
        handleBlur,
        multi,
        handleChange,
        selected,
        lineIndex,
        serviceCharge,
        standardConnectionCharge,
        invoice,
        disabled,
    } = props;

    return (
        <TableRow selected={selected} className={`data-row ${multi ? 'multi' : ''}`}>
            {
                lineIndex === 0 &&
                <TableCell className='waypoint fixed' rowSpan={2}>
                    {invoice.epNumber}
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell className='account' rowSpan={2}>
                    {invoice.accountNumber}
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell rowSpan={2}>
                    <input value={invoice.utilizedCapacity}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        name={`${index}.utilizedCapacity`}
                        onChange={onFieldChange}
                        onKeyDown={onPreventEnter}
                        onBlur={handleBlur}
                        type='number'
                        min='0'
                        step='any' />
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell rowSpan={2}>
                    <input value={invoice.demandReading}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        name={`${index}.demandReading`}
                        onChange={onFieldChange}
                        onKeyDown={onPreventEnter}
                        onBlur={handleBlur}
                        type='number'
                        min='0'
                        step='any' />
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell rowSpan={2}>
                    <input value={invoice.reactiveEnergy}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        name={`${index}.reactiveEnergy`}
                        onChange={onFieldChange}
                        onKeyDown={onPreventEnter}
                        onBlur={handleBlur}
                        type='number'
                        min='0'
                        step='any' />
                </TableCell>
            }
            <TableCell align='center'>
                <input value={value.days}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].days`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventDecimal}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='1' />
            </TableCell>
            <TableCell align='center'>
                <input value={value.adminCharge}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].adminCharge`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventEnter}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='any' />
            </TableCell>
            <TableCell>
                <input value={value.networkDemandUnits}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].networkDemandUnits`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventDecimal}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='1' />
            </TableCell>
            <TableCell className='disabled'>
                {value.ancillaryServiceCharge}
            </TableCell>
            <TableCell className='disabled'>
                {value.generationCapacityCharge}
            </TableCell>
            <TableCell>
                <input value={value.legacyCharge}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].legacyCharge`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventEnter}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='any' />
            </TableCell>
            <TableCell>
                <input value={value.unitsSTDLow}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].unitsSTDLow`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventDecimal}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='1' />
            </TableCell>
            <TableCell>
                <input value={value.unitsOffPeakLow}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].unitsOffPeakLow`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventDecimal}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='1' />
            </TableCell>
            <TableCell>
                <input value={value.unitsPeakLow}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].unitsPeakLow`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventDecimal}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='1' />
            </TableCell>
            <TableCell>
                <input value={value.unitsSTDHigh}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].unitsSTDHigh`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventDecimal}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='1' />
            </TableCell>
            <TableCell>
                <input value={value.unitsPeakHigh}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].unitsPeakHigh`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventDecimal}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='1' />
            </TableCell>
            <TableCell>
                <input value={value.unitsOffPeakHigh}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].unitsOffPeakHigh`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventDecimal}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='1' />
            </TableCell>
            {
                lineIndex === 0 &&
                <TableCell rowSpan={2}>
                    <input value={serviceCharge}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        name={`${index}.serviceCharge`}
                        onChange={onFieldChange}
                        onKeyDown={onPreventEnter}
                        onBlur={handleBlur}
                        type='number'
                        min='0'
                        step='any' />
                </TableCell>
            }
            <TableCell>
                <input value={value.excessNetworkAccessCharge}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].excessNetworkAccessCharge`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventEnter}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='any' />
            </TableCell>
            <TableCell>
                <input value={value.excessReactiveEnergy}
                    disabled={isSubmitting || disabled}
                    autoComplete='off'
                    name={`${index}.lines[${lineIndex}].excessReactiveEnergy`}
                    onChange={onFieldChange}
                    onKeyDown={onPreventEnter}
                    onBlur={handleBlur}
                    type='number'
                    min='0'
                    step='any' />
            </TableCell>
            {
                lineIndex === 0 &&
                <TableCell rowSpan={2}>
                    <input value={standardConnectionCharge}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        name={`${index}.standardConnectionCharge`}
                        onChange={onFieldChange}
                        onKeyDown={onPreventEnter}
                        onBlur={handleBlur}
                        type='number'
                        min='0'
                        step='any' />
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell rowSpan={2}>
                    <input value={value.interestCharge}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        name={`${index}.lines[${lineIndex}].interestCharge`}
                        onChange={onFieldChange}
                        onKeyDown={onPreventEnter}
                        onBlur={handleBlur}
                        type='number'
                        min='0'
                        step='any' />
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell className={`payment-due-date`} rowSpan={2}>
                    <div className='posr'>
                        {
                            !invoice.paymentDueDate &&
                            <InputMask for={`${index}.paymentDueDate`} mask='YYYY/MM/DD' className={`${isDueDateValid || !invoice.totalBilled ? '' : 'cr'}`} />
                        }
                    </div>
                    <DateTextInput value={invoice.paymentDueDate ?? ''}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        id={`${index}.paymentDueDate`}
                        name={`${index}.paymentDueDate`}
                        setFieldValue={props.setFieldValue}
                        onBlur={handleBlur}
                        className={`${isDueDateValid ? '' : 'cr'}`} />
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell className={`invoice-total ${disabled ? 'disabled' : ''}`} rowSpan={2}>
                    <input value={invoice.totalCharge}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        name={`${index}.totalCharge`}
                        onChange={handleManualChange}
                        type='number'
                        step='any'
                        className='tac'
                    />
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell className={`invoice-total ${disabled ? 'disabled' : ''}`} rowSpan={2}>
                    <input value={invoice.vatCharge}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        name={`${index}.vatCharge`}
                        onChange={handleManualChange}
                        type='number'
                        step='any'
                        className='tac'
                    />
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell className={`invoice-total ${disabled ? 'disabled' : ''}`} rowSpan={2}>
                    <input value={invoice.totalDue}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        name={`${index}.totalDue`}
                        onChange={handleManualChange}
                        type='number'
                        step='any'
                        className='tac'
                    />
                </TableCell>
            }
            {
                lineIndex === 0 &&
                <TableCell className='invoice-total' rowSpan={2}>
                    <input value={invoice.totalBilled}
                        disabled={isSubmitting || disabled}
                        autoComplete='off'
                        id={`${index}.totalBilled`}
                        name={`${index}.totalBilled`}
                        onChange={handleManualChange}
                        onBlur={handleBlur}
                        type='number'
                        min='0'
                        step='0.01' />
                </TableCell>
            }
        </TableRow>
    );
}

export default LPUCapturingFormTableRow;
